<template>
  <div class="chat-container">
    <div class="chat-header text-center mb-3">
      <h3 class="section-title mb-0">KECÁLEK</h3>
      <p class="text-muted small">Diskuse o všem a o ničem</p>
    </div>

    <div class="messages-window glass-panel" ref="messagesWindow">
      <div v-if="loading" class="text-center p-4">
        <div class="spinner-border text-danger" role="status"></div>
      </div>

      <div v-else-if="messages.length === 0" class="text-center text-muted p-5">
        <div class="mb-3">💬</div>
        Zatím tu nikdo nic nenapsal.<br />Buď první!
      </div>

      <div
        v-else
        v-for="msg in messages"
        :key="msg.id"
        class="message-row"
        :class="{ 'my-message': msg.userId === currentUserId }"
      >
        <div v-if="msg.userId !== currentUserId" class="avatar-col">
          <img
            v-if="msg.profilePicture"
            :src="msg.profilePicture"
            class="chat-avatar"
            :title="msg.username"
          />
          <div v-else class="chat-avatar-placeholder" :title="msg.username">
            {{ (msg.username || "?").charAt(0).toUpperCase() }}
          </div>
        </div>

        <div class="message-content">
          <div class="message-meta" v-if="msg.userId !== currentUserId">
            {{ msg.username }}
          </div>

          <div class="message-bubble">
            {{ msg.text }}
          </div>

          <div class="message-time">
            {{ formatTime(msg.createdAt) }}
          </div>
        </div>
      </div>
    </div>

    <form @submit.prevent="sendMessage" class="chat-input-area mt-3">
      <div class="input-group">
        <input
          type="text"
          v-model="newMessage"
          class="form-control dark-input chat-input"
          placeholder="Napiš zprávu..."
          required
        />
        <button
          type="submit"
          class="btn btn-red-glow send-btn"
          :disabled="!newMessage.trim() || sending"
        >
          <svg
            v-if="!sending"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc, // <--- PŘIDÁNO: Pro aktualizaci času návštěvy
} from "firebase/firestore";

export default {
  name: "CommunityChat",
  data() {
    return {
      messages: [],
      newMessage: "",
      loading: true,
      sending: false,
      currentUserId: auth.currentUser?.uid,
      unsubscribe: null,
      currentUserData: {
        username: "Neznámý",
        profilePicture: null,
      },
    };
  },
  async mounted() {
    await this.fetchCurrentUserDetails();
    this.subscribeToMessages();
    this.markAsRead(); // <--- NOVÉ: Zapsat, že jsem chat viděl
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    // NOVÁ METODA: Aktualizuje lastChatVisit v profilu uživatele
    async markAsRead() {
      if (!this.currentUserId) return;
      try {
        const userRef = doc(db, "users", this.currentUserId);
        // Zapíšeme aktuální čas serveru
        await updateDoc(userRef, {
          lastChatVisit: serverTimestamp(),
        });
      } catch (e) {
        console.error("Chyba při značení přečtení:", e);
      }
    },

    async fetchCurrentUserDetails() {
      if (!this.currentUserId) return;
      try {
        const snap = await getDoc(doc(db, "users", this.currentUserId));
        if (snap.exists()) {
          const data = snap.data();
          this.currentUserData = {
            username: data.username || "User",
            profilePicture: data.profilePicture || null,
          };
        }
      } catch (e) {
        console.error("Chyba profilu:", e);
      }
    },

    subscribeToMessages() {
      const q = query(
        collection(db, "chat_messages"),
        orderBy("createdAt", "asc"),
        limit(50)
      );

      this.unsubscribe = onSnapshot(q, (snapshot) => {
        this.messages = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        this.loading = false;
        this.scrollToBottom();

        // Když přijde nová zpráva a uživatel má chat zrovna otevřený,
        // můžeme znovu aktualizovat čas přečtení (volitelné, ale užitečné)
        if (document.visibilityState === "visible") {
          this.markAsRead();
        }
      });
    },

    async sendMessage() {
      if (!this.newMessage.trim()) return;
      this.sending = true;

      try {
        await addDoc(collection(db, "chat_messages"), {
          text: this.newMessage,
          userId: this.currentUserId,
          username: this.currentUserData.username,
          profilePicture: this.currentUserData.profilePicture,
          createdAt: serverTimestamp(),
        });

        this.newMessage = "";
        this.scrollToBottom();
        this.markAsRead(); // Po odeslání také označíme jako přečtené (pro jistotu)
      } catch (error) {
        console.error("Chyba odesílání:", error);
      } finally {
        this.sending = false;
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesWindow;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    formatTime(timestamp) {
      if (!timestamp) return "";
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

      return date.toLocaleTimeString("cs-CZ", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.chat-container {
  max-width: 800px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.messages-window {
  height: 60vh;
  overflow-y: auto;
  padding: 20px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Scrollbar */
.messages-window::-webkit-scrollbar {
  width: 6px;
}
.messages-window::-webkit-scrollbar-track {
  background: #111;
}
.messages-window::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 3px;
}

.message-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  max-width: 85%;
  align-self: flex-start;
}

.message-content {
  display: flex;
  flex-direction: column;
}

.message-bubble {
  background-color: #222;
  color: #fff;
  padding: 10px 15px;
  border-radius: 15px 15px 15px 2px;
  position: relative;
  word-wrap: break-word;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

/* Moje zpráva */
.my-message {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.my-message .message-bubble {
  background: linear-gradient(135deg, #a71d2a 0%, #dc3545 100%);
  color: white;
  border-radius: 15px 15px 2px 15px;
}

/* Jméno nad zprávou */
.message-meta {
  font-size: 0.75rem;
  color: #aaa;
  margin-bottom: 4px; /* Větší mezera od bubliny */
  margin-left: 10px; /* Zarovnání s bublinou */
  font-weight: bold;
}

.message-time {
  font-size: 0.65rem;
  color: #666;
  text-align: right;
  margin-top: 2px;
  margin-right: 2px;
}

/* Avatar */
.chat-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #444;
}

.chat-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #333;
  color: #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
}

.chat-input-area {
  background: #000;
  padding: 10px 0;
  position: sticky;
  bottom: 0;
}

.chat-input {
  border-radius: 25px 0 0 25px !important;
  padding-left: 20px;
}

.chat-input::placeholder {
  color: #aaa;
  opacity: 1;
}

.send-btn {
  border-radius: 0 25px 25px 0;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
